/**
 * IndiaHeatMap — Screening Concentration scatter (Feb-2026 redesign)
 * =====================================================================
 * Clean Recharts ScatterChart over India's lat/lng grid. No line
 * connections, no India outline polygon — just risk-coloured dots sized
 * by case count.
 */
import React from "react";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis,
  Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";

const CITIES = [
  { name: "Mumbai",    x: 72.8, y: 19.0, risk: "high", count: 12 },
  { name: "Delhi",     x: 77.2, y: 28.6, risk: "high", count: 18 },
  { name: "Bangalore", x: 77.6, y: 12.9, risk: "med",  count: 7  },
  { name: "Hyderabad", x: 78.5, y: 17.4, risk: "med",  count: 5  },
  { name: "Chennai",   x: 80.3, y: 13.1, risk: "low",  count: 3  },
  { name: "Kolkata",   x: 88.4, y: 22.6, risk: "med",  count: 6  },
  { name: "Pune",      x: 73.9, y: 18.5, risk: "low",  count: 4  },
  { name: "Jaipur",    x: 75.8, y: 26.9, risk: "high", count: 9  },
  { name: "Ahmedabad", x: 72.6, y: 23.0, risk: "med",  count: 5  },
  { name: "Lucknow",   x: 80.9, y: 26.9, risk: "low",  count: 2  },
];

const RISK_COLOR = { high: "#EF4444", med: "#F59E0B", low: "#10B981" };
const RISK_LABEL = { high: "High", med: "Med", low: "Low" };

function sizeForCount(count) {
  // Map 1..20 cases → 6..20 px (Recharts ZAxis uses square area;
  // we square the linear size to feed it.)
  const linear = 6 + Math.min(20, Math.max(0, count)) * 0.7; // 6..20
  return linear * linear;
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div data-testid="city-tooltip" style={{
      background: "#0d1117", border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: "8px", padding: "8px 12px", fontSize: "12px",
      color: "#f1f5f9", boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{p.name}</div>
      <div style={{ color: "#94A3B8" }}>
        {p.count} screening{p.count === 1 ? "" : "s"} ·{" "}
        <span style={{ color: RISK_COLOR[p.risk], fontWeight: 600 }}>
          {RISK_LABEL[p.risk]} risk
        </span>
      </div>
    </div>
  );
}

function CityLabel(props) {
  const { x, y, value } = props;
  return (
    <text x={x} y={y - 10} textAnchor="middle"
          fontSize={11} fill="#94A3B8"
          style={{ pointerEvents: "none" }}>
      {value}
    </text>
  );
}

export function IndiaHeatMap() {
  return (
    <div data-testid="india-heat-map"
         style={{
           position: "relative", width: "100%", height: "300px",
           padding: "12px",
           background: "transparent",
           border: "1px solid rgba(255,255,255,0.08)",
           borderRadius: "12px",
         }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 16, right: 24, bottom: 28, left: 8 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.04)" />
          <XAxis type="number" dataKey="x" name="Longitude"
                  domain={[68, 92]} tick={{ fill: "#475569", fontSize: 10 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                  label={{ value: "Longitude", position: "insideBottom",
                           offset: -8, fill: "#475569", fontSize: 10 }} />
          <YAxis type="number" dataKey="y" name="Latitude"
                  domain={[8, 32]} tick={{ fill: "#475569", fontSize: 10 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false} />
          <ZAxis type="number" dataKey="size" range={[36, 400]} />
          <Tooltip cursor={{ stroke: "rgba(255,255,255,0.1)", strokeDasharray: "3 3" }}
                    content={<CustomTooltip />} />
          <Scatter data={CITIES.map(c => ({ ...c, size: sizeForCount(c.count) }))}>
            {CITIES.map((c, i) => (
              <Cell key={i} fill={RISK_COLOR[c.risk]} fillOpacity={0.85}
                     stroke={RISK_COLOR[c.risk]} strokeWidth={1.5} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>

      {/* Legend overlay */}
      <div data-testid="city-map-legend"
           style={{
             position: "absolute", top: "16px", right: "16px",
             display: "flex", gap: "12px", alignItems: "center",
             background: "rgba(13,17,23,0.75)", border: "1px solid rgba(255,255,255,0.08)",
             borderRadius: "8px", padding: "4px 10px",
           }}>
        {["high", "med", "low"].map(r => (
          <div key={r} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%",
                            background: RISK_COLOR[r], display: "inline-block" }} />
            <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 500 }}>
              {RISK_LABEL[r]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default IndiaHeatMap;
